import axios from 'axios';

// API URL - Development ve Production için
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api' 
  : 'http://localhost:5000/api';

// Axios instance oluştur
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - token ekle
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - hata yönetimi
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // 401 hatası durumunda - sadece login sayfasında değilsek yönlendir
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const requestUrl = error.config?.url || '';
      
      // Public endpoint'ler için redirect yapma
      const publicEndpoints = ['/settings', '/products', '/products/featured'];
      const isPublicEndpoint = publicEndpoints.some(endpoint => requestUrl.includes(endpoint));
      
      // Login, register, forgot-password, order-success gibi sayfalardayken redirect yapma
      // Public endpoint'lerde redirect yapma
      if (!currentPath.startsWith('/login') && 
          !currentPath.startsWith('/register') && 
          !currentPath.startsWith('/forgot-password') &&
          !currentPath.startsWith('/reset-password') &&
          !currentPath.startsWith('/order-success') &&
          !isPublicEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('loginTime');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  verifyEmail: (email, code) => api.post('/auth/verify-email', { email, code }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  getMe: () => api.get('/auth/me'),
  updateProfile: (profileData) => api.put('/auth/profile', profileData),
  changePassword: (passwordData) => api.put('/auth/password', passwordData),
  forgotPasswordMe: () => api.post('/auth/forgot-password/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
};

// Products API
export const productsAPI = {
  getProducts: (params) => api.get('/products', { params }),
  getProduct: (id) => api.get(`/products/${id}`),
  getFeaturedProducts: () => api.get('/products/featured'),
  getCategories: () => api.get('/products/categories'),
  createProduct: (productData) => api.post('/products', productData),
  updateProduct: (id, productData) => api.put(`/products/${id}`, productData),
  deleteProduct: (id) => api.delete(`/products/${id}`),
  uploadImages: (id, formData) => api.post(`/products/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAdminReviews: (params) => api.get('/products/admin/reviews', { params }),
  getReviews: (id, params) => api.get(`/products/${id}/reviews`, { params }),
  getReviewEligibility: (id) => api.get(`/products/${id}/review-eligibility`),
  addReview: (id, reviewData) => api.post(`/products/${id}/reviews`, reviewData),
  // Admin API - Mevcut endpoint kullan (geçici çözüm)
  getAdminProducts: (params) => {
    const sortBy = params.sortBy || 'created_at';
    const sortOrder = params.sortOrder || 'DESC';
    let sort = 'newest';
    if (sortBy === 'name') {
      sort = sortOrder === 'ASC' ? 'name' : 'name_desc';
    } else if (sortBy === 'price') {
      sort = sortOrder === 'ASC' ? 'price-low' : 'price-high';
    } else if (sortBy === 'stock') {
      sort = sortOrder === 'ASC' ? 'stock_asc' : 'stock_desc';
    }

    const queryParams = {
      page: params.page || 1,
      limit: params.limit || 50,
      sort,
      admin: true
    };
    if (params.search) queryParams.search = params.search;
    if (params.category) queryParams.category = params.category;
    if (params.status) queryParams.status = params.status;
    if (params.stockStatus) queryParams.stockStatus = params.stockStatus;

    return api.get('/products', { params: queryParams });
  },
  getAdminStats: () => api.get('/products/admin/stats'),
};

// Cart API
export const cartAPI = {
  getCart: () => api.get('/cart'),
  addToCart: (productId, quantity) => api.post('/cart/add', { productId, quantity }),
  updateCartItem: (productId, quantity) => api.put('/cart/update', { productId, quantity }),
  removeFromCart: (productId) => api.delete(`/cart/remove/${productId}`),
  clearCart: () => api.delete('/cart/clear'),
  getCartCount: () => api.get('/cart/count'),
};

// Orders API
export const ordersAPI = {
  createOrder: (orderData) => api.post('/orders', orderData),
  createGuestOrder: (orderData) => api.post('/orders/guest', orderData), // Misafir kullanıcılar için
  getOrders: (params) => api.get('/orders', { params, timeout: 20000 }),
  getOrder: (id) => api.get(`/orders/${id}`),
  getPublicOrder: (id) => axios.get(`${API_URL}/orders/public/${id}`, { headers: { Authorization: null } }),
  updateOrderStatus: (id, statusData) => api.put(`/orders/${id}/status`, statusData),
  getAllOrders: (params) => api.get('/orders/admin/all', { params }),
  getOrderStats: () => api.get('/orders/admin/stats'),
};

// Payment API
export const paymentAPI = {
  initializeIyzico: (paymentData) => api.post('/payment/iyzico/initialize', paymentData),
  getPaymentStatus: (orderId) => api.get(`/payment/iyzico/status/${orderId}`),
};

// Users API
export const usersAPI = {
  getAllUsers: (params) => api.get('/users/admin/all', { params }),
  getUserStats: () => api.get('/users/admin/stats'),
  getUserDetails: (id) => api.get(`/users/admin/${id}`),
  updateUserStatus: (id, statusData) => api.put(`/users/admin/${id}/status`, statusData),
  updateUserRole: (id, roleData) => api.put(`/users/admin/${id}/role`, roleData),
  deleteUser: (id) => api.delete(`/users/admin/${id}`),
  getWishlist: () => api.get('/users/wishlist'),
  toggleWishlist: (productId) => api.post(`/users/wishlist/${productId}`),
};

// Settings API
export const settingsAPI = {
  getSettings: () =>
    api.get('/settings', {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache' }
    }),
  updateSettings: (settings) => api.put('/settings', settings)
};

// Favorites API
export const favoritesAPI = {
  getFavorites: () => api.get('/favorites'),
  addFavorite: (productId) => api.post('/favorites', { productId }),
  removeFavorite: (productId) => api.delete(`/favorites/${productId}`),
  checkFavorite: (productId) => api.get(`/favorites/check/${productId}`),
};

export default api;

